import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  linkedSignal,
  OnInit,
  TemplateRef,
  viewChild,
  inject,
  computed,
  effect,
  signal,
  ChangeDetectionStrategy,
  OnDestroy,
  INJECTOR,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SOFTLINE_FEATURE_UTILITIES_ACCOUNT } from '@softapps/allgemein/utils';
import { menuBar, MenuBarPicker, PickerOption, MenuBar } from '@softapps/core';
import { MasterDetailPageNew } from '@softapps/core/pages';
import {
  handleRequestError,
  refresh,
  Refreshable,
  RefreshService,
  title,
} from '@softline/application';
import {
  base64Decode,
  base64Encode,
  containsText,
  isDefinedNotEmpty,
  RemoteCollectionStore,
  Store,
} from '@softline/core';
import { IconComponent, translator } from '@softline/ui-core';
import { map } from 'rxjs/operators';
import { Ticket } from '../../data/ticket.model';
import { ContactTypePipe } from '../../pipes/contact-type.pipe';
import { queryParam } from '@softline/application';
import { TicketFilter } from '../../data/ticket-filter.model';
import { TicketQueryService } from '../../services/ticket-query.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { TicketQuery } from '../../data/ticket.query';
import { TicketService } from '../../services/ticket.service';
import { TicketDetailsComponent } from '../../components/ticket-details/ticket-details.component';
import { SOFTLINE_CONFIG_TICKET, TicketConfig } from '../../ticket.config';

import { ChipComponent } from '@softline/ui-core';
import { ChipGroupComponent } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { GroupByPipe } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { MasterDetailComponent } from '@softline/ui-core/data-view';
import { ToArrayPipe } from '@softline/ui-core';
import { ToolTipDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-ticket-overview',
  imports: [
    CommonModule,
    TicketDetailsComponent,
    ContactTypePipe,
    MenuBarPicker,
    PickerOption,
    MasterDetailPageNew,
    ChipComponent,
    ChipGroupComponent,
    FormatPipe,
    GroupByPipe,
    InputComponent,
    MasterDetailComponent,
    ToArrayPipe,
    ToolTipDirective,
    TranslatePipe,
    IconComponent,
  ],
  templateUrl: './ticket-overview.component.html',
  styleUrls: ['./ticket-overview.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: MenuBar,
      useExisting: forwardRef(() => TicketOverviewComponent),
    },
  ],
})
export class TicketOverviewComponent implements OnInit, OnDestroy, Refreshable {
  private injector = inject(INJECTOR);
  private store = inject(Store);
  protected router = inject(Router);
  private route = inject(ActivatedRoute);
  private ticketQueryService = inject(TicketQueryService);
  private ticketService = inject(TicketService);
  private ticketConfig: TicketConfig = inject(SOFTLINE_CONFIG_TICKET);
  private readonly refreshService = inject(RefreshService);

  public readonly all = this.ticketService.all;
  public readonly loading = this.ticketService.isLoading;

  id$ = this.route.paramMap.pipe(map((o) => o.get('id')));
  id = toSignal(this.id$);
  private t = translator();

  searchValue = signal<string>('');

  readonly query = queryParam('query');
  queryParams = computed<TicketQuery | undefined>(() => {
    const query = this.query();
    return query ? JSON.parse(base64Decode(query)) : undefined;
  });

  accountIdsLength$ = this.store
    .observe(SOFTLINE_FEATURE_UTILITIES_ACCOUNT, RemoteCollectionStore.getters.all)
    .pipe(map((o) => o?.length));

  private MY_TICKETS_IDX = this.ticketConfig?.selectedFilterIndex ?? 3;
  private resolverData = toSignal(this.route.data);
  filters = computed(() => this.resolverData()?.['ticketFilters'] as TicketFilter[]);
  selectedFilter = computed(
    () =>
      this.filters().find((f) => f.id === this.queryParams()?.filterId) ??
      this.filters()[this.MY_TICKETS_IDX]
  );

  filteredTickets = computed<Ticket[]>(() => {
    return this.all()
      .filter((ticket) => {
        return (
          ticket.id.toString().indexOf(this.searchValue().toLowerCase()) > -1 ||
          containsText(ticket, this.searchValue(), true)
        );
      })
      .sort((a, b) => b.id - a.id);
  });

  readonly #selectedTicket = computed<Ticket | null>(() => {
    const id = this.queryParams()?.id + '';

    if (isDefinedNotEmpty(id)) {
      return this.all().find((t) => t.id.toString() === id) ?? null;
    } else {
      return this.all()[this.MY_TICKETS_IDX];
    }
  });

  readonly selectedTicket = linkedSignal(() => {
    return this.#selectedTicket();
  });

  readonly idFunction = (ticket: Ticket) => ticket.id + '';

  readonly menuBarChipsTemplate = viewChild.required('menuBarChipsTemplate', {
    read: TemplateRef,
  });
  readonly menuBarFilterTemplate = viewChild.required('menuBarFilterTemplate', {
    read: TemplateRef,
  });
  constructor() {
    title(this.t('#TICKETS.TITLE'));
    refresh(async () => {
      this.load();
    });
    menuBar({
      key: 'customers/softline/ticket',
      title: this.t('Aktionen'),
      template: this.menuBarFilterTemplate,
      align: 'leading',
      separated: false,
    });

    menuBar({
      key: 'customers/softline/ticket',
      title: this.t('Aktionen'),
      template: this.menuBarChipsTemplate,
      align: 'leading',
      separated: false,
    });
    effect(() => {
      const queryParams = this.queryParams();
      this.ticketQueryService.setQueryParam(queryParams ?? {});
    });
  }

  async onSelectFilter(option: TicketFilter): Promise<void> {
    this.router.navigate(['./'], {
      queryParams: {
        query: base64Encode(
          JSON.stringify({ ...this.queryParams(), filterId: +option.id })
        ),
      },
      relativeTo: this.route,
    });
    this.selectedTicket.set(null);
    this.searchValue.set('');
  }

  async ngOnInit(): Promise<void> {
    this.refreshService.add(this);
    this.load();
  }

  ngOnDestroy(): void {
    this.refreshService.remove(this);
  }

  load(): void {
    const queryParams = this.queryParams();

    if (!queryParams?.filterId) {
      this.ticketQueryService.setQueryParam({
        filterId: this.filters()[this.MY_TICKETS_IDX]?.id,
      });
    }
  }

  async refresh(): Promise<void> {
    try {
      this.load();
      this.ticketService.reload();
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }


  async resetFilters(): Promise<void> {
    await this.router.navigate(['./'], {
      queryParams: {
        query: base64Encode(JSON.stringify({ filterId: null })),
      },
      relativeTo: this.route,
    });
  }

  async removeQueryProperty(
    query: TicketQuery,
    ...propertyNames: string[]
  ): Promise<void> {
    query = { ...query };

    for (const propertyName of propertyNames) {
      query[propertyName] = null;
    }

    await this.router.navigate(['./'], {
      queryParams: {
        query: base64Encode(JSON.stringify(query)),
      },
      relativeTo: this.route,
    });
  }
}
